<template>
    <component :is="isMainContent ? 'app-content' : 'div'" :breadcrumb="breadcrumb" :class="!workGroupCreation ? 'module-shift-schedule' : ''"
        :headline="title">
        <loading-indicator v-if="loading"/>
        <div v-else :class="{'row': !workGroupCreation}">
            <div :class="{'col-lg-12 col-xl-9': !workGroupCreation}">
                <form ref="form" @submit.prevent="saveEntry">
                    <div id="shift-edit">
                        <portuna-box :title="title" class="xxl-padding" title-icon="table_chart">
                            <div class="row">
                                <div class="col-md-9">
                                    <div v-if="id" class="row">
                                        <div class="col-md-6 default-margin-bottom">
                                            <strong class="color-primary">Arbeitsbereich: {{workGroupName}}</strong>
                                        </div>
                                    </div>

                                    <div class="row">
                                        <div class="col-md-6">
                                            <form-row error-name="schedule.entry_start" label="Datum" label-for="start_date">
                                                <form-input-date v-model="inputEditDate" :min="todayDate" name="schedule[entry_start]"/>
                                            </form-row>
                                        </div>

                                        <div class="col-md-3">
                                            <form-row error-name="schedule.entry_start_time" label="Startzeit" label-for="start_time">
                                                <form-input-date v-model="inputStartTime" name="schedule[entry_start_time]" type="time"/>
                                            </form-row>
                                        </div>

                                        <div class="col-md-3">
                                            <form-row error-name="schedule.entry_end_time" label="Endzeit" label-for="end_time">
                                                <form-input-date v-model="inputEndTime" name="schedule[entry_end_time]" type="time"/>
                                            </form-row>
                                        </div>
                                    </div>

                                    <template v-if="!id">
                                        <div class="form-item-spacer">&nbsp;</div>

                                        <form-row error-name="work_group_id" label="Arbeitsbereich" label-for="work_group_id" disabled>
                                            <form-select v-model="entry.work_group_id" :options="workGroupOptions" type="hidden"/>
                                        </form-row>
                                    </template>

                                    <form-input :model-value="entry.work_group_id" name="work_group_id" type="hidden"/>

                                    <form-row v-if="isCreatePage" error-name="role_ids" label="Rollen" label-for="role_ids">
                                        <form-select v-model="inputRoleIds" :options="roleOptions" multiple name="role_ids[]"/>
                                    </form-row>
                                    <form-row v-else error-name="role_id" label="Rolle" label-for="role_id">
                                        <form-select v-model="inputRoleId" :options="roleOptions" name="role_id"/>
                                    </form-row>

                                    <form-row error-name="required_employees" label="Anzahl benötigter Personen pro Rolle" label-for="required_employees">
                                        <div class="row">
                                            <div class="col-md-2">
                                                <form-input v-model="entry.required_employees" :max="workGroupEmployeeCount" min="1"
                                                    name="required_employees" type="number"/>
                                            </div>
                                        </div>
                                    </form-row>

                                    <form-row error-name="schedule" label="Wiederholungen" label-for="schedule">
                                        <form-schedule v-model="entry.schedule"/>
                                    </form-row>
                                </div>
                            </div>
                        </portuna-box>

                        <form-submit-row
                            v-if="isEditPage || !workGroupCreation"
                            :delete-callback="isEditPage ? deleteEntry : null"
                            :disable-delete="!isCreatePage"
                            :redirect-on-cancel="{name: 'shift-schedule-calendar'}"
                            :show-action-apply="false"
                            :show-action-save-options="false"
                            @submit="submitForm"/>
                    </div>
                </form>
            </div>
        </div>
    </component>
</template>

<script lang="ts">
    import {formSubmit, getFormData, parseFormData} from '@/library/form';
    import Schedule from '@/library/form/schedule';
    import {getEmployeeBasicBreadcrumb} from '@/library/modules/employee/employee';
    import {NoOperation} from '@/library/utils';
    import {deepCopyObject, errorHandler, sendPopupRequest, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import axios, {AxiosError, type AxiosResponse} from 'axios';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import {Action} from 'vuex-facing-decorator';
    import AppContent from '../../components/AppContent.vue';
    import FieldsetCloneable from '../../components/FieldsetCloneable.vue';
    import FormError from '../../components/form/FormError.vue';
    import FormInput from '../../components/form/FormInput.vue';
    import FormInputDate from '../../components/form/FormInputDate.vue';
    import FormRow from '../../components/form/FormRow.vue';
    import FormSchedule from '../../components/form/FormSchedule.vue';
    import FormSelect from '../../components/form/FormSelect.vue';
    import FormSelectEmployee from '../../components/form/FormSelectEmployee.vue';
    import FormSelectEntity from '../../components/form/FormSelectEntity.vue';
    import FormSteps from '../../components/form/FormSteps.vue';
    import FormSubmitRow from '../../components/form/FormSubmitRow.vue';
    import LoadingIndicator from '../../components/LoadingIndicator.vue';
    import PortunaBox from '../../components/PortunaBox.vue';
    import RelativeDateTime from '../../components/RelativeDateTime.vue';
    import TabBarContent from '../../components/TabBarContent.vue';
    import TabBarLabel from '../../components/TabBarLabel.vue';
    import TabBarLabels from '../../components/TabBarLabels.vue';
    import TopMessageHandler from '../../library/topMessageHandler';
    import dateHelper from '../../utils/portuna/dateHelper';

    @Component({
        name: 'ShiftEdit',
        components: {
            FormError,
            FormSchedule,
            FormSelect,
            RelativeDateTime,
            FormInputDate,
            TabBarLabels,
            TabBarLabel,
            TabBarContent,
            FormSteps,
            FieldsetCloneable,
            FormSelectEmployee,
            FormSelectEntity,
            FormInput,
            FormRow,
            LoadingIndicator,
            PortunaBox,
            FormSubmitRow,
            AppContent,
        },
        methods: {
            dateHelper,
        },
    })
    export class ShiftEdit extends Vue
    {
        readonly $refs: {
            form: HTMLFormElement,
        };

        @Action('errorPage') errorPage: (error: AxiosError | AxiosResponse) => void;

        @Prop({
            type: Number,
            required: true,
        }) id: number;

        @Prop({
            type: Number,
            default: 0,
            required: false,
        }) workGroupId: number;

        @Prop({
            type: Boolean,
            default: true,
            required: false,
        }) isMainContent: boolean;

        @Prop({
            type: String,
            default: null,
            required: false,
        }) editDate: Nullable<string>;

        @Prop({
            type: Boolean,
            default: false,
            required: false,
        }) workGroupCreation: boolean;

        @Prop({
            type: Number,
            default: null,
            required: false,
        }) roleId: Nullable<number>;

        @Prop({
            type: String,
            default: null,
            required: false,
        }) startTime: Nullable<string>;

        @Prop({
            type: String,
            default: null,
            required: false,
        }) endTime: Nullable<string>;

        viewName: string = 'ShiftEdit';

        loading = true;

        entry = {
            schedule: {},
        } as ShiftEntry;

        oldEntry = {
            schedule: {},
        } as ShiftEntry;

        selects: Record<'workGroups' | 'roles', SelectOption[]> = {
            workGroups: [],
            roles: [],
        };

        idCopy: number = 0;

        workGroupIdCopy: number = 0;

        workGroupEmployeeCount = 0;

        roleAssignments: Record<string, number[]> = {};

        inputEditDate: Nullable<string> = null;

        inputStartTime: Nullable<string> = null;

        inputEndTime: Nullable<string> = null;

        inputRoleId: Nullable<number> = null;

        inputRoleIds: number[] = [];

        serial: number = 0;

        get title(): string
        {
            return `Schicht ${this.isCreatePage ? 'anlegen' : 'bearbeiten'}`;
        }

        get breadcrumb(): Breadcrumb[]
        {
            return [
                getEmployeeBasicBreadcrumb(),
                {
                    label: 'Schichtplanung',
                    href: {name: 'shift-schedule-calendar'},
                },
            ];
        }

        get roleOptions(): SelectOption[]
        {
            if (!this.entry
                || !this.entry.work_group_id
                || 'undefined' === typeof this.roleAssignments[this.entry.work_group_id]
                || !this.roleAssignments[this.entry.work_group_id].length)
            {
                return [];
            }

            return this.selects.roles.filter(item => this.roleAssignments[this.entry.work_group_id].includes(~~item.value));
        }

        get workGroupOptions(): SelectOption[]
        {
            if (this.isMainContent)
            {
                return this.selects.workGroups;
            }

            return this.selects.workGroups.filter(item => item.value === this.workGroupId);
        }

        get isEditPage(): boolean
        {
            return this.isMainContent && !!this.idCopy;
        }

        get isCreatePage(): boolean
        {
            return !this.idCopy;
        }

        get workGroupName(): string
        {
            let workGroupName = '';

            for (const workGroupSelectItem of this.workGroupOptions)
            {
                if (workGroupSelectItem.value === this.entry.work_group_id)
                {
                    workGroupName = workGroupSelectItem.label;
                }
            }

            return workGroupName;
        }

        get roleName(): string
        {
            let roleName = '';

            for (const roleSelectItem of this.roleOptions)
            {
                if (roleSelectItem.value === this.entry.role_id)
                {
                    roleName = roleSelectItem.label;
                }
            }

            return roleName;
        }

        get todayDate(): string
        {
            return dateHelper('Y-m-d') as string;
        }

        @Watch('id')
        watchId(): void
        {
            this.fetchData();
        }

        @Watch('workGroupId')
        watchWorkGroupId(): void
        {
            this.fetchData();
        }

        created(): void
        {
            this.idCopy          = this.id;
            this.workGroupIdCopy = this.workGroupId;

            this.fetchData();
        }

        fetchData(): void
        {
            this.loading    = true;
            const id        = this.id === 0 ? null : this.id;
            let workGroupId = this.workGroupId === 0 ? null : this.workGroupId;

            if (!this.isCreatePage)
            {
                workGroupId = this.entry.work_group_id;
            }

            this.axios.get('/employee/shift-schedule/shift/edit/id/' + id + '/work-group-id/' + workGroupId + '/date/' + this.editDate).then(response =>
            {
                const {
                          data,
                          status,
                      } = response.data;

                if (XHR_STATUS_DEFAULT !== status)
                {
                    throw response;
                }

                this.$store.dispatch('form/setFilter', data.filter);

                this.entry                  = data.entry;
                this.oldEntry               = deepCopyObject(data.entry);
                this.selects                = data.selects;
                this.roleAssignments        = data.role_assignments;
                this.workGroupEmployeeCount = data.work_group_employee_count;
            })
            .catch(this.errorPage)
            .finally(() =>
            {
                this.inputEditDate  = this.editDate;
                this.inputStartTime = this.idCopy ? this.entry.schedule.entry_start_time : this.startTime;
                this.inputEndTime   = this.idCopy ? this.entry.schedule.entry_end_time : this.endTime;

                if (this.isCreatePage && this.roleId)
                {
                    this.inputRoleIds.push(this.roleId);
                }
                else
                {
                    this.inputRoleId = this.idCopy ? this.entry.role_id : this.roleId;
                }

                this.loading = false;
            });
        }

        cancel(): void
        {
            this.routerGoBack({name: 'shift-schedule-calendar'});
        }

        deleteEntry(): void
        {
            if (this.editDate === null)
            {
                return;
            }

            if (this.oldEntry.schedule.custom_repeat_type !== 'none')
            {
                Schedule.showChoosePopup('', 'delete', 'feminine', 'Schicht', 'Schicht')
                .then(serial =>
                {
                    this.serial = serial ? 1 : 0;
                    axios.post('/employee/shift-schedule/shift/delete/id/' + this.id + '/date/' + this.editDate + '/serial/' + this.serial).then(() => {
                        this.redirect({name: 'shift-schedule-calendar'});
                    });
                })
                .catch(NoOperation);
            }
            else
            {
                sendPopupRequest({
                    url: '/employee/shift-schedule/shift/delete/id/' + this.id + '/date/' + this.editDate,
                    message: {
                        headline: 'Löschen',
                        message: 'Sind Sie sicher, dass Sie für den Arbeitsbereich '
                            + '<' + 'strong>'
                            + this.workGroupName
                            + '<' + '/strong>'
                            + ' und der Rolle '
                            + '<' + 'strong>'
                            + this.roleName
                            + ' ALLE <' + '/strong> Schichten von '
                            + '<' + 'strong>'
                            + this.entry.schedule.entry_start_time
                            + '-'
                            + this.entry.schedule.entry_end_time
                            + '<'
                            + '/strong>, ab dem <strong>'
                            + dateHelper('d.m.Y', this.editDate)
                            + '<'
                            + '/strong>, löschen möchten?',
                        success: 'Die Schicht wurde gelöscht.',
                        failed: 'Die Schicht konnte nicht gelöscht werden.',
                    },
                    options: {
                        textIcon: 'delete_forever',
                        textIconClass: 'text-danger',
                        titleIcon: 'delete',
                        buttonOkay: {
                            type: 'danger',
                            icon: 'delete',
                            title: 'Löschen',
                        },
                    },
                })
                .then(() =>
                {
                    let params = {};

                    if (this.editDate !== null)
                    {
                        params = {date: this.editDate};
                    }

                    this.$router.push({
                        name: 'shift-schedule-calendar',
                        params: params,
                    });
                })
                .catch(errorHandler);
            }
        }

        save(formSteps: Nullable<FormSteps>, serial: Nullable<number>, additionallyData?: [] | {}): Promise<any>
        {
            return new Promise((resolve, reject) =>
            {
                const formData = getFormData(this.$refs.form, additionallyData);
                return formSubmit('/employee/shift-schedule/shift/edit/id/' + this.id + '/work-group-id/' + (this.isCreatePage ? this.workGroupId : this.entry.work_group_id) + '/serial/' + serial + '/date/' + this.editDate, {formSteps}, parseFormData(formData))
                .then((data: any) =>
                {
                    TopMessageHandler.success(data.message);
                    resolve(data);
                })
                .catch(error =>
                {
                    errorHandler(error);
                    reject(error);
                });
            });
        }

        submitForm(): void
        {
            if (!this.id || this.oldEntry.schedule.custom_repeat_type === 'none')
            {
                this.saveEntry(0);

                return;
            }

            Schedule.showChoosePopup('', 'modify', 'feminine', 'Schicht', 'Schicht')
            .then(serial =>
            {
                this.serial = serial ? 1 : 0;
                this.saveEntry(this.serial);
            })
            .catch(NoOperation);
        }

        saveEntry(serial: Nullable<number>): void
        {
            this.save(null, serial, []).then(() =>
            {
                let params = {};

                if (this.editDate !== null)
                {
                    params = {date: this.editDate};
                }

                this.$router.push({
                    name: 'shift-schedule-calendar',
                    params: params,
                });
            });
        }
    }

    export default ShiftEdit;
</script>

